import { useState, useEffect, useMemo, useRef } from 'react';
import { Tree, TreeSelectionEvent, TreeCheckboxSelectionKeys, TreeMultipleSelectionKeys } from 'primereact/tree';
import TreeNode from 'primereact/treenode';
import { Button } from 'primereact/button';
import { Toolbar } from 'primereact/toolbar';
import { Fieldset } from 'primereact/fieldset';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Toast } from 'primereact/toast';
import { InputSwitch } from 'primereact/inputswitch';
import samplePageService from './sample-page-service';
import { RadioButton } from 'primereact/radiobutton';
import { useToast } from '../../../components/Toast';
import { useLoading } from '../../../components/loading/loadingContext';
import { Calendar } from 'primereact/calendar';
import RequiredLabel from '../../../components/RequiredLabel';
import BreadcrumbComponent from '../../../components/Breadcrumb';
import { useDialog } from '../../../components/Dialog';
import { SPexceptions } from './SPexceptions';


interface DropdownItem {
    referenceCode: string;
    fullDesc: string;
}

const SamplePageEdit: React.FC = () => {
    const [clientState, setClientState] = useState<any>(null);
    const navigate = useNavigate();
    const toast = useRef<Toast>(null);
    const { showToast } = useToast();
    const { showExceptionDialog, showConfirmRemarkDialog, showConfirmDialog } = useDialog();
    const location = useLocation();
    const selectedItem = location.state?.selectedItem;
    const isEdit = !!selectedItem;

    const [formData, setFormData] = useState<any>(null);

    const [genderDropdown, setGenderDropdown] = useState<DropdownItem[]>([]);
    const genderDropdownOptions = useMemo(() => genderDropdown, [genderDropdown]);

    const [errors, setErrors] = useState<{ [key: string]: string }>({});
    const requiredFields = ["required"];

    const defaultData = {
        radio: "A",
        status: "1",
    };

    useEffect(() => {
        const fetchClientState = async () => {
            try {
                samplePageService.getClientState().then((data) => {
                    setClientState(data);
                });

                samplePageService.getGenderDropdown('ADP', 'GENDER').then((data) => {
                    setGenderDropdown(data);
                });

                if (isEdit) {
                    setFormData(selectedItem);
                } else {
                    setFormData(defaultData);
                };


            } catch (error) {
                clientState.error.detail = error;
                showToast(clientState.error);
            }
        };

        fetchClientState();
    }, []);

    const handleInputChange = (e: any) => {
        const { id, value } = e.target;
        setFormData({
            ...formData,
            [id]: value
        });
    };

    const handleUpperCaseInputChange = (e: any) => {
        const { id, value } = e.target;
        setFormData({
            ...formData,
            [id]: value.toUpperCase(),
        });
    };

    const handleDropdownChange = (e: any) => {
        const { id, value } = e.target;
        setFormData({
            ...formData,
            [id]: value
        });
    };

    const handleBirthDateInputChange = (e: any) => {
        const { id, value } = e.target;
        setFormData({
            ...formData,
            [id]: value,
            age: calculateAge(value),
        });
    };

    const calculateAge = (dob: Date | null): string => {
        if (!dob) return "";

        const today = new Date();
        const birthDate = new Date(dob);

        let years = today.getFullYear() - birthDate.getFullYear();
        let months = today.getMonth() - birthDate.getMonth();
        let days = today.getDate() - birthDate.getDate();

        // Adjust if the birth date is in a future month
        if (months < 0 || (months === 0 && days < 0)) {
            years--;
            months += 12;
        }

        // Adjust if the birth date is in a future day of the month
        if (days < 0) {
            const prevMonth = new Date(today.getFullYear(), today.getMonth(), 0);
            days += prevMonth.getDate();
            months--;
        }

        return `${years} Years, ${months} Months, ${days} Days`;
    };

    const footerTemplate = () => {
        function submit(): void {
            let newErrors: { [key: string]: string } = {};
            requiredFields.forEach((field) => {
                if (!formData[field]) {
                    newErrors[field] = "This is a required field.";
                    showToast(clientState.required);
                }
            });

            setErrors(newErrors);

            if (Object.keys(newErrors).length === 0) {

                const currentDate = new Date().toLocaleString("en-US", { timeZone: clientState.timeZone });

                formData.modifiedDate = new Date(currentDate);
                formData.modifiedBy = clientState.userId;

                if (!isEdit) {
                    formData.insertDate = new Date(currentDate);
                    formData.insertBy = clientState.userId;
                    formData.facilityCode = clientState.facilityCode;

                    samplePageService.create(formData).then(() => {
                        showToast(clientState.success);
                        navigate(`/uikit/sample-page`);

                    }).catch((error) => {
                        clientState.error.detail = error.response?.data?.message || 'Failed to create record.';;
                        showToast(clientState.error);
                    });
                } else {
                    samplePageService.update(formData).then(() => {
                        navigate(`/uikit/sample-page`);
                        showToast(clientState.success);
                    }).catch((error) => {
                        clientState.error.detail = error.response?.data?.message || 'Failed to update record.';;
                        showToast(clientState.error);
                    });
                }
            }
        }

        function submitException(): void {
            showExceptionDialog(101, SPexceptions);
        }

        function submitConfirm(): void {
            showConfirmDialog("Confirmation", "Are you sure you want to save this?", () => {
                console.log("Deleted!");
            });
        }

        function submitRemark(): void {
            showConfirmRemarkDialog("Are you sure you want to proceed?", (remark?: string) => {
                console.log("Confirmed with remark:", remark);
            });
        }
        function handleBack(): void {
            navigate(`/uikit/sample-page`);
        }

        return (
            <>
                <Toast ref={toast} />
                <Button label="Save" icon="pi pi-check" style={{ marginRight: '.5em' }} className="p-button-success" onClick={submit} />
                <Button label="Save With Confirm" icon="pi pi-check" style={{ marginRight: '.5em' }} onClick={submitConfirm} />
                <Button label="Save With Bussiness Rule" icon="pi pi-check" style={{ marginRight: '.5em' }} onClick={submitException} />

                <Button label="Save With Remark" icon="pi pi-check" style={{ marginRight: '.5em' }} onClick={submitRemark} />
                <Button label="Back" icon="pi pi-trash" style={{ marginRight: '.5em' }} onClick={() => handleBack()} />

            </>
        );
    };

    return (
        <div className="grid">
            <div className='col-12'>
                <div className='card-title'>
                    <h5>Sample Form</h5>
                    <BreadcrumbComponent />
                </div>
                <div className='form-title'>
                    <Fieldset legend="Form Information">
                        <div className="p-fluid form-grid grid">
                            <div className="field col-12 md:col-6">
                                <label>Radio Button</label>
                                <div className="grid radio-group">
                                    <div className="col-12 md:col-3">
                                        <div className="field-radiobutton">
                                            <RadioButton
                                                inputId="option1"
                                                name="option"
                                                value="A"
                                                checked={formData?.radio === "A"}
                                                onChange={(e) => setFormData({ ...formData, radio: e.value })}
                                            />
                                            <label htmlFor="option1">A for All</label>
                                        </div>
                                    </div>
                                    <div className="col-12 md:col-3">
                                        <div className="field-radiobutton">
                                            <RadioButton
                                                inputId="option1"
                                                name="option"
                                                value="B"
                                                checked={formData?.radio === "B"}
                                                onChange={(e) => setFormData({ ...formData, radio: e.value })}
                                            />
                                            <label htmlFor="option1">B for Ball</label>
                                        </div>
                                    </div>
                                    <div className="col-12 md:col-3">
                                        <div className="field-radiobutton">
                                            <RadioButton
                                                inputId="option1"
                                                name="option"
                                                value="C"
                                                checked={formData?.radio === "C"}
                                                onChange={(e) => setFormData({ ...formData, radio: e.value })}
                                            />
                                            <label htmlFor="option1">C for Call</label>
                                        </div>
                                    </div>
                                    <div className="col-12 md:col-3">
                                        <div className="field-radiobutton">
                                            <RadioButton
                                                inputId="option1"
                                                name="option"
                                                value="D"
                                                checked={formData?.radio === "D"}
                                                onChange={(e) => setFormData({ ...formData, radio: e.value })}
                                            />
                                            <label htmlFor="option1">D for Dall</label>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="field col-12 md:col-6">
                                <h6>Status</h6>
                                <InputSwitch
                                    checked={formData?.status === "1"}
                                    onChange={(e) => setFormData({ ...formData, status: e.value ? "1" : "0" })}
                                />
                            </div>
                            <div className="field col-12 md:col-6">
                                <label>Form</label>
                                <InputText
                                    id="form"
                                    value={formData?.form || ''}
                                    onChange={handleInputChange}
                                />
                            </div>
                            <div className="field col-12 md:col-6">
                                <label>FORM UPPER CASE</label>
                                <InputText
                                    id="formUpperCase"
                                    value={formData?.formUpperCase || ''}
                                    onChange={handleUpperCaseInputChange}
                                />
                            </div>
                            <div className="field col-12 md:col-6">
                                <RequiredLabel text="Required" required />
                                <InputText
                                    id="required"
                                    value={formData?.required || ''}
                                    onChange={handleInputChange}
                                />
                                {errors.required && <small className="p-error">{errors.required}</small>}
                            </div>
                            <div className="field col-12 md:col-6">
                                <label htmlFor="gender">Reference Code Dropdown (Gender)</label>
                                <Dropdown
                                    id="gender"
                                    value={formData?.gender || ''}
                                    onChange={handleDropdownChange}
                                    options={genderDropdownOptions}
                                    optionLabel="fullDesc"
                                    placeholder="Select One"
                                    optionValue="referenceCode"
                                >
                                </Dropdown>
                            </div>
                            <div className="field col-12 md:col-6">
                                <label> Date</label>
                                <Calendar
                                    id="date"
                                    value={formData?.date ? new Date(formData.date) : null}
                                    onChange={(e) => setFormData({ ...formData, date: e.value })}
                                    dateFormat="dd M yy"
                                    showIcon
                                >
                                </Calendar>
                            </div>
                            <div className="field col-12 md:col-6">
                                <label>Date With Business Rule</label>
                                <Calendar
                                    id="birthDate"
                                    value={formData?.birthDate ? new Date(formData.birthDate) : null}
                                    onChange={handleBirthDateInputChange}
                                    dateFormat="dd M yy"
                                    showIcon
                                >
                                </Calendar>
                            </div>
                            <div className="field col-12 md:col-6">
                                <label>Age (Disable)</label>
                                <InputText
                                    id="age"
                                    value={formData?.age || ''}
                                    disabled
                                />
                            </div>
                        </div>
                    </Fieldset>
                    <div>
                        <br />
                    </div>
                    <Toolbar end={footerTemplate}></Toolbar>
                </div>
            </div>
        </div>

    );
};

export default SamplePageEdit;
